import { FC, ReactNode, useCallback, useContext, useMemo } from "react";

import { Box } from "@mui/material";
import {
  ensureAbsoluteUrl,
  FormCategory,
  FormType,
  getDonationOrTicketingFormLink,
  ORGANIZATION_COUNTRY_MAP,
  PaymentMethod,
  parseFormBackgroundTheme,
  QuestionType,
} from "@simplyk/common";
import { inferRouterOutputs } from "@trpc/server";
import { useSearchParams } from "next/navigation";
import {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormReturn,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";

import { useFrontendAuctionContext } from "../../../../../../common/contexts/FrontendAuctionContext";
import { FrontendFormContext } from "../../../../../../common/contexts/FrontendFormContext";
import { useFormV2Context, useTicketingFormV2ContextOrDefaults } from "../../../contexts/FormV2Context";
import { GiveAsSection } from "../GiveAsSection/GiveAsSection";
import { useGiveAsSectionRenderPosition } from "../GiveAsSection/useGiveAsSectionRenderPosition";
import { SectionWrapper } from "../SectionWrapper";

import { useAnimationShapesOnTipChange } from "@/canvas/components/BackgroundShapes/useAnimationShapesOnTipChange";
import type { PaymentErrorContextType } from "@/canvas/features/FormV2/blocks/payment/PaymentSection/PaymentElement/PaymentErrorContext";
import { getDonationSelectionTracking } from "@/canvas/features/FormV2/helpers/getDonationSelectionTracking";
import { useAmplitudePaymentEventLog } from "@/canvas/features/FormV2/blocks/payment/PaymentSection/PaymentElement/useAmplitudePaymentEventLog";
import { StripePaymentComponent } from "@/canvas/features/FormV2/blocks/payment/PaymentSection/StripePaymentComponent/StripePaymentComponent";
import type {
  OnPaymentSubmitResult,
  PaymentSubmitData,
} from "@/canvas/features/FormV2/blocks/payment/PaymentSection/StripePaymentComponent/StripePaymentComponentProps";
import { splitCartQuantities } from "@/canvas/features/FormV2/helpers/splitCartQuantities";
import { buildPostFormSubmitUrl } from "@/canvas/features/PostFormSubmit/helpers/postFormSubmitUrl";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { SessionContext } from "@/common/contexts/SessionContext";
import { StripeContext } from "@/common/contexts/StripeContext";
import { pushGtmCommandSubmittedSuccessfully } from "@/common/helpers/gtm";
import { useMembershipRebuy } from "@/canvas/features/FormV2/hooks/useMembershipRebuy";
import {
  getNextPaymentDateParam,
  getThankYouPageUrl,
  hasCumulativeReceiptPayment,
} from "@/common/helpers/thankYouPage";
import { isSmsOptInPhoneMissing } from "@/common/helpers/validators";
import { AppRouterType } from "@/common/helpers/trpc";
import { FormOrganizationOutput } from "@/common/types/trpc";
import { useDurationOnPage } from "@/common/hooks/useDurationOnPage";
import { useIsEmbeddedModal } from "@/common/hooks/useEmbeddedModal";
import { useFormQueryParams } from "@/common/hooks/useFormParams";
import { useRegistrationRedirect } from "@/common/hooks/useRegistrationRedirect";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { SubmitCommandFormInput } from "@/components/PaymentProcessor/helper";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useSubmitCommand } from "@/components/PaymentProcessor/hooks/useSubmitCommand";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { isPaymentFailed } from "@/components/PaymentProcessor/type";
type SubmitCommandOutput = inferRouterOutputs<AppRouterType>["form_submitCommand"];

type PaymentSectionProps = {
  paymentErrorState: PaymentErrorContextType;
  isLiveFormEditorPreview: boolean;
  isPaymentSectionGhostPreview: boolean;
  eligibilitySlot?: ReactNode;
};

export const PaymentSection: FC<PaymentSectionProps> = ({
  paymentErrorState,
  isLiveFormEditorPreview,
  isPaymentSectionGhostPreview,
  eligibilitySlot,
}) => {
  const { t } = useTranslate();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());

  const {
    displayExpressCheckout,
    supportsExpressCheckout,
    isEmbed,
    isEmbedV2,
    embedId,
    displayedFormAmountWithTip,
    displayedFormAmount,
    currency,
    organization,
    formData,
    formType,
    category,
    commandId,
    tip,
    selectedTip,
    shouldSendTipPercentage,
    generateETicket,
    themeColor,
    postTransactionUrl,
    setIsSubmitting,
    setIsPadLoading,
    paymentMode,
    stripeRecurrenceInterval,
    formAmplitudeContext,
    formEmbedVersion,
    prioritizeBankPayment,
    fees,
    formattedFees,
    updateFreeTipAmount,
    isMembershipV2,
    isFreeTicketing,
    allowedPaymentMethods,
    isPaymentDisabled,
    isArchived,
    isDraft,
    handleSetSupportsExpressCheckout,
    isExpressCheckoutLoading,
    expressCheckoutDisabled,
    expressCheckoutIsSelected,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    isOnboarding,
    chequeDescription,
    isPreviewTemplateMode,
    getTipChoice,
    displayAddressQuestion,
  } = useContext(FrontendFormContext);

  const {
    validDiscount,
    ticketingEvent,
    hasADate,
    localeField: ticketingField,
    formObject: ticketing,
    addOnRates,
    ticketsPurchased,
  } = useTicketingFormV2ContextOrDefaults();

  const addOnRateIds = useMemo(() => new Set(addOnRates.map((rate) => rate.id)), [addOnRates]);
  const { itemsCount: cartItemsCount, addOnsCount: cartAddOnsCount } = splitCartQuantities(
    ticketsPurchased,
    addOnRateIds
  );

  const { sessionId } = useContext(SessionContext);
  const { locale, isoLocale } = useLocaleContext();
  const { getDurationTimeOnPage } = useDurationOnPage();
  const { impactChainUtmSourceEmail, source } = useFormQueryParams();
  const isEmbeddedModal = useIsEmbeddedModal(query);
  const { shouldRedirectToPeerToPeer, getRegistrationRedirectUrl } = useRegistrationRedirect();
  const stripeCtx = useContext(StripeContext);

  const nestedInFormStripeTree = useMemo(
    () => ({
      hasPad: stripeCtx.hasPad,
      hasAch: stripeCtx.hasAch,
      stripePaymentMethodTypes: stripeCtx.stripePaymentMethodTypes,
      stripePaymentMethods: stripeCtx.stripePaymentMethods,
      zeffyPaymentMethods: stripeCtx.zeffyPaymentMethods,
      setExpressCheckoutWallet: stripeCtx.setExpressCheckoutWallet,
    }),
    [
      stripeCtx.hasPad,
      stripeCtx.hasAch,
      stripeCtx.stripePaymentMethodTypes,
      stripeCtx.stripePaymentMethods,
      stripeCtx.zeffyPaymentMethods,
      stripeCtx.setExpressCheckoutWallet,
    ]
  );

  const formV2Context = useFormV2Context();
  const getMembershipRebuy = useMembershipRebuy();
  const {
    form,
    formObject,
    type,
    formCategory,
    isAuction: ctxIsAuction,
    currentStep,
    questions,
    formCountry,
  } = formV2Context;
  const suggestedAmountsExperimentData =
    formV2Context.type === FormType.DonationForm ? formV2Context.suggestedAmountsExperimentData : null;
  const { hasAlreadyBid } = useFrontendAuctionContext();
  const { formState } = form;
  const { handleSubmitCommand } = useSubmitCommand({
    setValue: form.setValue as UseFormSetValue<TicketingPaymentInput | DonationFormPaymentInput>,
  });

  const amplitudeEventLogModel = useMemo(
    () => ({
      commandId,
      tip,
      selectedPaymentMethod,
      isEmbed,
      category,
      formAmplitudeContext,
      displayedFormAmount,
      getTipChoice,
      selectedTip,
      displayAddressQuestion,
      organization,
      currency,
      ticketing: { extraDonation: ticketing.extraDonation, thermometerTarget: ticketing.thermometerTarget },
      itemsCount: cartItemsCount,
      addOnsCount: cartAddOnsCount,
      locale,
      isoLocale,
      source,
      impactChainUtmSourceEmail,
      expressCheckoutWallet: stripeCtx.expressCheckoutWallet,
      formObject,
      suggestedAmountsExperimentData,
      getDonationSelection: () => getDonationSelectionTracking(formV2Context),
    }),
    [
      commandId,
      tip,
      selectedPaymentMethod,
      isEmbed,
      category,
      formAmplitudeContext,
      displayedFormAmount,
      getTipChoice,
      selectedTip,
      displayAddressQuestion,
      organization,
      currency,
      ticketing.extraDonation,
      ticketing.thermometerTarget,
      cartItemsCount,
      cartAddOnsCount,
      locale,
      isoLocale,
      source,
      impactChainUtmSourceEmail,
      stripeCtx.expressCheckoutWallet,
      formObject,
      suggestedAmountsExperimentData,
      formV2Context,
    ]
  );
  const { logDonorFormSubmitted } = useAmplitudePaymentEventLog({
    formStateErrors: form.formState.errors,
    formObject,
    model: amplitudeEventLogModel,
  });

  const isFreeTicket = displayedFormAmount === 0;
  const giveAsSectionRenderPosition = useGiveAsSectionRenderPosition();

  const amplitudePayload = useMemo(
    () => ({
      isEmbed,
      formCategory,
      formId: formObject.id,
      organizationCountry: formObject.organization?.country,
    }),
    [isEmbed, formCategory, formObject.id, formObject.organization?.country]
  );

  useAnimationShapesOnTipChange();

  const getThankYouUrl = useCallback(
    ({
      command,
      paymentInput,
      formType: ft,
      organization: org,
      emailUpdateToken,
      buyerTicketToken,
    }: {
      command?: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"];
      paymentInput: TicketingPaymentInput | DonationFormPaymentInput;
      formType: FormType;
      organization: FormOrganizationOutput;
      emailUpdateToken?: string;
      buyerTicketToken?: string;
    }): string => {
      return getThankYouPageUrl({
        command: command as NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"],
        formType: ft,
        organization: org,
        postTransactionUrl,
        isEmbeddedModal,
        generateETicket,
        event: ticketingEvent,
        formData,
        isoLocale,
        category,
        isAuction: ctxIsAuction,
        themeColor,
        isDarkMode: formObject.isDarkMode || false,
        backgroundTheme: parseFormBackgroundTheme(formObject.backgroundTheme),
        isEmbed,
        embedId: embedId ?? undefined,
        emailUpdateToken,
        buyerTicketToken,
        membershipRebuy: getMembershipRebuy(command, paymentInput),
        nextPaymentDate: getNextPaymentDateParam(paymentInput),
        hasCumulativeReceipt: hasCumulativeReceiptPayment(paymentInput),
      });
    },
    [
      postTransactionUrl,
      isEmbeddedModal,
      generateETicket,
      ticketingEvent,
      formData,
      isoLocale,
      category,
      ctxIsAuction,
      themeColor,
      formObject.isDarkMode,
      formObject.backgroundTheme,
      isEmbed,
      embedId,
      getMembershipRebuy,
    ]
  );

  const buildCashAppReturnUrl = useCallback(
    ({
      command,
      transactionId,
      paymentInput,
      emailUpdateToken,
      buyerTicketToken,
    }: {
      command: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"];
      transactionId: string | undefined;
      paymentInput: TicketingPaymentInput | DonationFormPaymentInput;
      emailUpdateToken?: string;
      buyerTicketToken?: string;
    }) => {
      const registrationRedirectUrl = shouldRedirectToPeerToPeer(paymentInput, formType)
        ? getRegistrationRedirectUrl({
            firstName: command.firstName,
            lastName: command.lastName,
            email: command.email,
            commandId: command.id,
          })
        : null;
      const thankYouUrl =
        registrationRedirectUrl ??
        getThankYouUrl({
          command,
          paymentInput,
          formType,
          organization,
          emailUpdateToken,
          buyerTicketToken,
        });
      const formUrl = getDonationOrTicketingFormLink({
        form: {
          id: formData.id,
          path: formData.path,
          formType,
          formCategory: category as FormCategory,
          isPrimaryCampaign: undefined,
          isPrimaryTeam: undefined,
        },
        isoLocale,
      });
      return buildPostFormSubmitUrl({
        commandId,
        formId: formData.id,
        formType,
        formCategory: category,
        isoLocale,
        thankYouUrl: thankYouUrl || "",
        formUrl,
        organizationName: organization.name,
        hasADate,
        generateETicket: generateETicket && command.productsAmount > 0,
        generateTaxReceipt: (command.eligibleAmount || 0) > 0 && organization.canGenerateReceipt,
        organizationId: organization.id,
        transactionId,
        productsAmount: command.productsAmount,
        extraDonation: command.extraDonation,
        currency: command.currency,
      });
    },
    [
      getThankYouUrl,
      shouldRedirectToPeerToPeer,
      getRegistrationRedirectUrl,
      formData.id,
      formData.path,
      formType,
      category,
      isoLocale,
      commandId,
      organization,
      hasADate,
      generateETicket,
    ]
  );

  const buildSubmitCommandFormInput = useCallback(
    ({
      paymentInput,
      paymentMethod,
      stripePaymentMethodId: stripePaymentMethodIdArg,
      cardBrand: cardBrandArg,
      tipAmount: tipAmountArg,
      tipPercentage: tipPercentageArg,
      isExpressCheckout,
    }: {
      paymentInput: DonationFormPaymentInput | TicketingPaymentInput;
      paymentMethod: PaymentMethod;
      stripePaymentMethodId: string | null;
      cardBrand: string | null;
      tipAmount: number;
      tipPercentage: number;
      isExpressCheckout: boolean;
    }): SubmitCommandFormInput => ({
      paymentInput,
      paymentMethod,
      formObject,
      user: null,
      contact: null,
      formType: type,
      validDiscount: validDiscount ?? null,
      session: { sessionId, durationOnPage: getDurationTimeOnPage() },
      routerQuery: query,
      commandId,
      locale,
      tip: tipAmountArg,
      tipPercentage: tipPercentageArg,
      stripePaymentMethodId: stripePaymentMethodIdArg ?? null,
      cardBrand: cardBrandArg,
      isExpressCheckout,
      formEmbedVersion: formEmbedVersion ?? undefined,
    }),
    [formObject, validDiscount, sessionId, getDurationTimeOnPage, query, commandId, locale, type, formEmbedVersion]
  );

  const onPaymentSubmit = useCallback(
    async (data: PaymentSubmitData): Promise<OnPaymentSubmitResult> => {
      const smsPaymentInput = data.paymentInput as Partial<TicketingPaymentInput>;
      const notificationQuestionId = questions.find(
        (question) => question.type === QuestionType.NotificationPhoneNumber
      )?.id;
      if (
        isSmsOptInPhoneMissing({
          smsOptIn: smsPaymentInput.smsOptIn,
          answers: smsPaymentInput.customAnswersOfOrder ?? [],
          notificationQuestionId,
          iso2Country: ORGANIZATION_COUNTRY_MAP[formCountry].countryCode,
        })
      ) {
        return { error: t("ticketing", "smsOptIn.phoneRequiredWhenOptedIn"), code: "sms_opt_in_requires_phone" };
      }

      const submitCommandInput = buildSubmitCommandFormInput({
        paymentInput: data.paymentInput,
        paymentMethod: data.paymentMethod,
        stripePaymentMethodId: data.stripePaymentMethodId,
        cardBrand: data.cardBrand,
        tipAmount: data.tipAmount,
        tipPercentage: data.tipPercentage,
        isExpressCheckout: data.isExpressCheckout,
      });

      const res = await handleSubmitCommand(submitCommandInput);
      if (isPaymentFailed(res)) {
        return { error: res.error.message, code: res.error.code, declineCode: res.error.declineCode };
      }
      if (!res.data) {
        return { error: t("common", "unknown_error"), code: "unknown" };
      }
      const d = res.data;
      let cashAppReturnUrl: string | undefined;
      if (data.paymentMethod === PaymentMethod.CashApp && d.command) {
        cashAppReturnUrl = buildCashAppReturnUrl({
          command: d.command,
          transactionId: d.transaction?.id,
          paymentInput: data.paymentInput,
          emailUpdateToken: d.emailUpdateToken,
          buyerTicketToken: d.buyerTicketToken,
        });
      }
      return { ...d, cashAppReturnUrl };
    },
    [buildSubmitCommandFormInput, handleSubmitCommand, t, buildCashAppReturnUrl, questions, formCountry]
  );

  const onPaymentSucceeded = useCallback(
    async ({
      paymentInput,
      command,
      transaction,
      formType: ft,
      isExpressCheckout,
      emailUpdateToken,
      buyerTicketToken,
    }: {
      paymentInput: TicketingPaymentInput | DonationFormPaymentInput;
      command?: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"];
      transaction: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["transaction"] | null;
      formType: FormType;
      isExpressCheckout?: boolean;
      emailUpdateToken?: string;
      buyerTicketToken?: string;
    }) => {
      logDonorFormSubmitted({
        isExpressCheckout: Boolean(isExpressCheckout),
        isPaymentSucceeded: true,
      });
      pushGtmCommandSubmittedSuccessfully({
        organization_id: organization?.id ?? "",
        form_id: formData.id,
        form_type: ft,
        form_category: category,
        amount: command?.productsAmount ? command.productsAmount / 100 : 0,
        extra_donation: command?.extraDonation ? command.extraDonation / 100 : 0,
        currency: command?.currency ?? undefined,
        transaction_id: transaction?.id ?? "",
      });

      if (shouldRedirectToPeerToPeer(paymentInput, ft)) {
        const redirectUrl = getRegistrationRedirectUrl({
          firstName: command?.firstName,
          lastName: command?.lastName,
          email: command?.email,
          commandId: command?.id,
        });
        if (redirectUrl) {
          return { returnUrl: redirectUrl };
        }
        return { error: "No associated peer-to-peer campaign found" };
      }
      const thankYouUrl = getThankYouUrl({
        command,
        paymentInput,
        formType: ft,
        organization,
        emailUpdateToken,
        buyerTicketToken,
      });

      const returnUrl = ensureAbsoluteUrl(postTransactionUrl) || thankYouUrl;

      return { returnUrl };
    },
    [
      logDonorFormSubmitted,
      organization,
      formData,
      category,
      shouldRedirectToPeerToPeer,
      getThankYouUrl,
      postTransactionUrl,
      getRegistrationRedirectUrl,
    ]
  );

  const isPaypalEligible =
    Boolean(organization.featurePaypalEnabled) &&
    Boolean(organization.country) &&
    !stripeRecurrenceInterval &&
    !isMembershipV2;

  const tipConfig = useMemo(
    () => ({
      amount: tip,
      percentage: selectedTip.percentage ?? 0,
      shouldSendTipPercentage,
      fees,
      formattedFees: formattedFees ?? "",
      updateFreeTipAmount,
    }),
    [tip, selectedTip.percentage, shouldSendTipPercentage, fees, formattedFees, updateFreeTipAmount]
  );

  if (
    currentStep !== "selection" &&
    ((type === FormType.Ticketing && isFreeTicket) || (ctxIsAuction && hasAlreadyBid))
  ) {
    return <>{eligibilitySlot}</>;
  }

  return (
    <SectionWrapper
      title={t("common", "paymentMethod")}
      isGhostPreview={isPaymentSectionGhostPreview}
      dataTest="form-v2-payment-section"
    >
      {eligibilitySlot}
      {giveAsSectionRenderPosition === "express-checkout" && (
        <Box marginBottom={2}>
          <GiveAsSection
            form={form as UseFormReturn<TicketingPaymentInput | DonationFormPaymentInput>}
            amplitudePayload={amplitudePayload}
            formType={type}
          />
        </Box>
      )}

      <StripePaymentComponent<TicketingPaymentInput | DonationFormPaymentInput>
        amount={displayedFormAmountWithTip}
        displayedFormSubtotalCents={displayedFormAmount}
        selectedTip={selectedTip}
        displayAddressQuestion={displayAddressQuestion}
        getTipChoice={getTipChoice}
        currency={currency}
        country={organization.country}
        organization={organization}
        formType={formType}
        category={category}
        formData={{ id: formData.id, path: formData.path }}
        commandId={commandId}
        formAmplitudeContext={formAmplitudeContext}
        tip={tip}
        isEmbed={isEmbed}
        isEmbedV2={isEmbedV2}
        stripeRecurrenceInterval={stripeRecurrenceInterval}
        postTransactionUrl={postTransactionUrl}
        hasADate={hasADate}
        generateETicket={generateETicket}
        isPaymentDisabled={Boolean(isPaymentDisabled)}
        isArchived={isArchived}
        isDraft={Boolean(isDraft)}
        formObject={formObject}
        buildSubmitCommandFormInput={buildSubmitCommandFormInput}
        handleSubmit={form.handleSubmit as UseFormHandleSubmit<TicketingPaymentInput | DonationFormPaymentInput>}
        setValue={form.setValue as UseFormSetValue<TicketingPaymentInput | DonationFormPaymentInput>}
        trigger={form.trigger as UseFormTrigger<TicketingPaymentInput | DonationFormPaymentInput>}
        getValues={form.getValues as UseFormGetValues<TicketingPaymentInput | DonationFormPaymentInput>}
        control={form.control as Control<TicketingPaymentInput | DonationFormPaymentInput>}
        formStateErrors={formState.errors as FieldErrors<TicketingPaymentInput | DonationFormPaymentInput>}
        onPaymentSubmit={onPaymentSubmit}
        onPaymentSucceeded={onPaymentSucceeded}
        allowedPaymentMethods={allowedPaymentMethods}
        paymentMode={paymentMode}
        prioritizeBankPayment={prioritizeBankPayment}
        isPayPalEnabled={isPaypalEligible}
        isFreeTicketing={isFreeTicketing}
        isPreviewTemplateMode={isPreviewTemplateMode}
        tipConfig={tipConfig}
        isOnboarding={isOnboarding}
        offlinePaymentWording={ticketingField?.offlinePaymentWording}
        chequeDescription={chequeDescription}
        shouldDisplaySubmitButton={false}
        setIsSubmitting={setIsSubmitting}
        setIsPadLoading={setIsPadLoading}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        nestedInFormStripeTree={nestedInFormStripeTree}
        formPathExpressCheckout={{
          expressCheckoutDisabled,
          isExpressCheckoutLoading,
          supportsExpressCheckout,
          displayExpressCheckout,
          handleSetSupportsExpressCheckout,
          expressCheckoutIsSelected,
        }}
        expressCheckoutAnalyticsContext={{
          currentStep,
          formType: type,
          formObjectId: formObject.id,
        }}
        paymentErrorState={paymentErrorState}
        amplitudeTicketingForPaymentLog={{
          extraDonation: ticketing.extraDonation,
          thermometerTarget: ticketing.thermometerTarget,
        }}
        formQueryUtmForPaymentLog={{ source, impactChainUtmSourceEmail }}
        isLiveFormEditorPreview={isLiveFormEditorPreview}
      />
    </SectionWrapper>
  );
};

/** Re-exported so `SubmitCommandButton` and other form paths can keep using `PaymentElement` (knip entry). */
export { PaymentElement } from "./PaymentElement/PaymentElement";
